/** 사용량 — the portfolio view of first-party in-app telemetry (S7, `/analytics`).
 *
 *  Apps are keyed by bundle id — what the app itself reports — and joined to
 *  the ASO `apps` table on that same key, so a row links straight to the ASO
 *  page and back. "지금 수집" goes out to the collector worker, so a dev server
 *  never presses it. */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, RefreshCw } from 'lucide-react'
import { api, type AnalyticsAppSummary } from '@/api/client'
import { formatRelative, fromUnixMs } from '@/lib/time'
import { qk, useAnalyticsStatus } from '@/lib/queries'
import {
  AppIcon,
  Button,
  ConfirmDialog,
  DataTable,
  Disclosure,
  EmptyState,
  errorText,
  ErrorState,
  Notice,
  FilterBar,
  FilterSelect,
  PageHeader,
  StatTile,
  TonePill,
  useToast,
  type Column,
} from '@/ui'
import { statusOf } from '@/lib/status'
import { SessionBars } from './charts'
import { Freshness } from './AnalyticsPanel'
import {
  analyticsQuery,
  DETAIL_DAYS,
  fmtDuration,
  fmtNum,
  fmtPct,
  LIST_DAYS,
  platformLabel,
  platformsOf,
  rangeLabel,
  rangeTitle,
  UNPAIRED_WARN,
  windowLabel,
} from './format'
import { LinkAppCell } from './LinkAppCell'
import { usePlatform, useWindowDays } from './params'
import { PlatformPicker, WindowPicker } from './pickers'
import { QualityCard } from './QualityCard'
import { ScreenBreakdown } from './ScreenBreakdown'
import { SortLabel, sortRows, useTableSort } from './sort'
import { UsageTabs } from './web/shared'

/** Sortable columns of the apps table. The trend is deliberately absent: it is
 *  a series, with no single value to order by. */
type AppsSort = 'app' | 'platform' | 'sessions' | 'users' | 'avg' | 'paywall' | 'bought' | 'unpaired'
const APPS_TEXT: readonly AppsSort[] = ['app', 'platform']
const SORT_LABEL: Record<AppsSort, string> = {
  app: '앱 이름',
  platform: '플랫폼',
  sessions: '세션',
  users: '사용자',
  avg: '평균 세션',
  paywall: '페이월',
  bought: '구매',
  unpaired: '짝 없음',
}

function appsValue(a: AnalyticsAppSummary, key: AppsSort): number | string {
  switch (key) {
    case 'app':
      // Sort by what the row shows: the linked store name when there is one,
      // the bundle id when the app is not tracked for ASO yet.
      return a.linked?.name ?? a.app_id
    case 'platform':
      return a.platforms
    case 'sessions':
      return a.sessions
    case 'users':
      return a.users
    case 'avg':
      return a.avg_session_ms
    case 'paywall':
      return a.paywall_shown
    case 'bought':
      return a.purchase_success
    case 'unpaired':
      return a.unpaired_enter_rate
  }
}

export default function AnalyticsList() {
  const qc = useQueryClient()
  const toast = useToast()
  const [days, setDays] = useWindowDays(LIST_DAYS)
  const [platform, setPlatform] = usePlatform()
  const [confirm, setConfirm] = useState<'pull' | 'backfill' | null>(null)

  const apps = useQuery({
    queryKey: qk.analyticsApps(days, platform),
    queryFn: () => api.analyticsApps(days, platform),
    placeholderData: keepPreviousData,
  })
  const status = useAnalyticsStatus()
  const quality = useQuery({ queryKey: qk.analyticsQuality(days), queryFn: () => api.analyticsQuality(days) })
  const breakdown = useQuery({
    queryKey: qk.analyticsBreakdown(days, platform),
    queryFn: () => api.analyticsBreakdown(days, platform),
    placeholderData: keepPreviousData,
  })

  const invalidateAll = () => {
    for (const key of ['analytics-apps', 'analytics-status', 'analytics-quality', 'analytics-app', 'analytics-breakdown']) {
      qc.invalidateQueries({ queryKey: [key] })
    }
  }

  const pull = useMutation({
    mutationFn: () => api.analyticsPull(),
    onSuccess: (r) => {
      invalidateAll()
      toast.success(`수집했다. 새 이벤트 ${fmtNum(r.inserted)}건, 중복 ${fmtNum(r.duplicates)}건`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  // Aggregates are computed once per day and then kept. When a rule that
  // shapes them changes — the dwell ceiling, the too-short floor — past days
  // keep their old answer until they are recomputed. It reads the raw events
  // already stored here; nothing goes out.
  const backfill = useMutation({
    mutationFn: () => api.analyticsBackfill(30),
    onSuccess: (r) => {
      invalidateAll()
      toast.success(`${r.days.length}일을 다시 계산했다`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const all = apps.data?.items ?? []
  // Options come from the response's own `platforms`, not from the rows: each
  // row's `platforms` is scoped by the active filter, so deriving the options
  // from them made the picker delete its own options the moment one was
  // clicked.
  const platformOptions = apps.data?.platforms ?? []
  // The server already scoped these rows AND their numbers. Filtering here
  // instead was the old bug: it picked the right apps and then printed each
  // one's cross-platform totals beside them. Ordering is the client's
  // business: the whole window is here already. Opens on sessions, descending,
  // the order the server sends.
  const { sort, pick } = useTableSort<AppsSort>({ key: 'sessions', dir: 'desc' }, APPS_TEXT)
  const items = useMemo(() => sortRows(all, sort, appsValue, (a) => a.app_id), [all, sort])
  const totalSessions = items.reduce((n, a) => n + a.sessions, 0)
  const totalUsers = items.reduce((n, a) => n + a.users, 0)
  const lastEventAt = items.reduce((n, a) => Math.max(n, a.last_event_at), 0)
  // Every row carries the same zero-filled window, so any row names its span.
  const windowDays = all[0]?.daily ?? []
  const st = status.data
  const busy = pull.isPending || !!st?.running
  const pulled = fromUnixMs(st?.last_pull_at ? st.last_pull_at * 1000 : 0)

  const head = (k: AppsSort, label: string, title?: string) => (
    <SortLabel k={k} sort={sort} onPick={pick} title={title}>
      {label}
    </SortLabel>
  )
  const detailHref = (a: AnalyticsAppSummary) =>
    `/analytics/${encodeURIComponent(a.app_id)}${analyticsQuery(days, platform, DETAIL_DAYS)}`

  const columns: Column<AnalyticsAppSummary>[] = [
    {
      key: 'app',
      header: head('app', '앱'),
      card: 'title',
      cell: (a) => (
        <Link to={detailHref(a)} className="flex min-w-0 items-center gap-2.5 hover:text-accent">
          <AppIcon src={a.linked?.icon_url} name={a.linked?.name ?? a.app_id} size={28} />
          <span className="min-w-0">
            <span className="block truncate font-medium">{a.linked?.name ?? a.app_id}</span>
            <span className="block truncate font-mono text-micro text-muted-foreground">{a.app_id}</span>
          </span>
        </Link>
      ),
    },
    {
      key: 'platform',
      header: head('platform', '플랫폼'),
      card: 'meta',
      cell: (a) => (
        <span className="flex flex-wrap gap-1">
          {platformsOf(a.platforms).map((p) => (
            <TonePill key={p} tone="neutral" label={platformLabel(p)} />
          ))}
        </span>
      ),
    },
    {
      key: 'trend',
      header: <span title="기간 안 UTC 일별 세션. 가장 새 막대가 오른쪽이다.">추이</span>,
      // A series has no single value to order by, so it stays unsorted rather
      // than sorting by something the column does not show.
      card: 'line',
      cell: (a) => <SessionBars series={a.daily ?? []} />,
    },
    { key: 'sessions', header: head('sessions', '세션'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (a) => fmtNum(a.sessions) },
    { key: 'users', header: head('users', '사용자'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (a) => fmtNum(a.users) },
    // the phone pairs the counts that are read together; sorting stays on the
    // filter row's 정렬
    {
      key: 'sessionsUsers',
      header: '세션 · 사용자',
      cardOnly: true,
      cell: (a) => (
        <span className="tabular-nums">
          {fmtNum(a.sessions)} <span className="text-muted-foreground">·</span> {fmtNum(a.users)}
        </span>
      ),
    },
    { key: 'avg', header: head('avg', '평균 세션'), align: 'right', className: 'tabular-nums', card: 'line', cell: (a) => fmtDuration(a.avg_session_ms) },
    { key: 'paywall', header: head('paywall', '페이월'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (a) => fmtNum(a.paywall_shown) },
    { key: 'bought', header: head('bought', '구매'), align: 'right', className: 'tabular-nums', card: 'hidden', cell: (a) => fmtNum(a.purchase_success) },
    {
      key: 'paywallBought',
      header: '페이월 · 구매',
      cardOnly: true,
      cell: (a) => (
        <span className="tabular-nums">
          {fmtNum(a.paywall_shown)} <span className="text-muted-foreground">·</span> {fmtNum(a.purchase_success)}
        </span>
      ),
    },
    {
      key: 'unpaired',
      header: head('unpaired', '짝 없음', '종료가 짝으로 붙지 않은 화면 진입의 비율. 오르면 앱이 깨졌다는 뜻이다.'),
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      // on a card only when it warns
      cardHide: (a) => a.unpaired_enter_rate < UNPAIRED_WARN,
      cell: (a) =>
        a.unpaired_enter_rate >= UNPAIRED_WARN ? (
          <TonePill
            tone={statusOf('quality', 'warn').tone}
            label={fmtPct(a.unpaired_enter_rate, 0)}
            className="tabular-nums"
          />
        ) : (
          <span className="text-muted-foreground">{fmtPct(a.unpaired_enter_rate, 0)}</span>
        ),
    },
    {
      key: 'link',
      header: '연결',
      align: 'right',
      card: 'actions',
      cell: (a) => <LinkAppCell appId={a.app_id} linked={a.linked} />,
    },
  ]

  return (
    <>
      <PageHeader
        title="사용량"
        description="앱이 직접 보내는 사용 기록. 번들 ID로 ASO 앱과 이어진다."
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshCw}
              loading={backfill.isPending}
              disabled={busy}
              title="원본 이벤트로 최근 30일을 다시 계산한다. 체류나 세션을 세는 규칙을 바꾼 뒤에 필요하다."
              onClick={() => setConfirm('backfill')}
            >
              {backfill.isPending ? '계산 중' : '다시 계산'}
            </Button>
            <Button variant="primary" icon={Download} loading={busy} onClick={() => setConfirm('pull')}>
              {busy ? '수집 중' : '지금 수집'}
            </Button>
          </>
        }
      >
        <FilterBar
          end={
            <p className="text-meta text-muted-foreground">
              {windowDays.length > 0 && (
                <>
                  <span title={rangeTitle(windowDays)}>{rangeLabel(windowDays)}</span> ·{' '}
                </>
              )}
              <Freshness lastEventAt={lastEventAt} />
            </p>
          }
        >
          <UsageTabs value="apps" />
          <PlatformPicker platform={platform} options={platformOptions} onChange={setPlatform} />
          <WindowPicker days={days} onChange={setDays} />
          <FilterSelect
            label="앱 표 정렬"
            value={sort.key}
            onChange={(v) => pick(v as AppsSort)}
            className="md:hidden"
            options={(Object.keys(SORT_LABEL) as AppsSort[]).map((k) => ({ value: k, label: `정렬 · ${SORT_LABEL[k]}` }))}
          />
        </FilterBar>
      </PageHeader>

      <div className="space-y-6">
        {st && !st.configured && (
          <p className="rounded-lg border border-border bg-card px-4 py-3 text-table text-muted-foreground">
            수집기가 아직 설정되지 않았다.{' '}
            <Link to="/settings?tab=analytics" className="text-accent hover:underline">
              설정의 자체 분석 수집기
            </Link>
            에서 워커 주소와 풀 토큰을 넣고 켠다. 그 전에는 스케줄러가 5분마다 실패하지 않고 건너뛴다.
          </p>
        )}

        {backfill.isError && <ErrorState title="다시 계산하지 못했다" error={backfill.error} />}
        {backfill.isSuccess && (
          <p className="rounded-lg border border-border bg-card px-4 py-3 text-table text-muted-foreground">
            {backfill.data.days.length}일을 다시 계산했다. 이벤트 {fmtNum(backfill.data.events)}건에서 세션{' '}
            {fmtNum(backfill.data.sessions)}개.
          </p>
        )}
        {pull.isError && <ErrorState title="수집하지 못했다" error={pull.error} />}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="보고하는 앱" value={items.length} />
          <StatTile label={`세션 · ${windowLabel(days)}`} value={fmtNum(totalSessions)} />
          <StatTile label={`사용자 · ${windowLabel(days)}`} value={fmtNum(totalUsers)} />
          <StatTile
            label="가장 새 이벤트"
            value={lastEventAt ? formatRelative(fromUnixMs(lastEventAt)) : '—'}
            // last_pull_at is epoch SECONDS; last_event_at is milliseconds.
            hint={pulled ? `수집기가 ${formatRelative(pulled)} 가져옴 · 5분마다` : '수집기는 5분마다 가져온다'}
          />
        </div>

        {st && !st.last_ok && st.last_error && (
          <Notice tone="failed" title="마지막 수집이 실패했다" detail={st.last_error}>
            커서는 앞으로 가지 않았다. 잃은 것은 없고 다음 실행이 같은 자리에서 이어진다.
          </Notice>
        )}

        <section aria-label="앱" className="space-y-2">
          <div className="flex items-baseline gap-x-2">
            <h2 className="text-section font-semibold">앱</h2>
            <span className="text-meta text-muted-foreground">번들 ID 하나가 한 줄</span>
          </div>
          <DataTable
            label="사용량 앱"
            rows={apps.data ? items : undefined}
            rowKey={(a) => a.app_id}
            columns={columns}
            loading={apps.isLoading}
            error={apps.error}
            onRetry={() => apps.refetch()}
            empty={
              platform ? (
                <EmptyState
                  title={`이 기간에 ${platformLabel(platform)} 앱이 없다`}
                  description="다른 플랫폼에는 데이터가 있을 수 있다. 필터를 풀면 보인다."
                />
              ) : (
                <EmptyState
                  title="아직 사용 기록이 없다"
                  description="앱에 분석 전송 코드가 들어가고 수집기 설정이 끝나면 기록이 들어온다."
                />
              )
            }
          />
        </section>

        <ScreenBreakdown data={breakdown.data} loading={breakdown.isLoading} />

        {/* Quality rows carry a single platform each, so the same filter
            applies: leaving them unfiltered would contradict the table above. */}
        <QualityCard rows={(quality.data?.items ?? []).filter((r) => !platform || r.platform.toLowerCase() === platform)} showApp />

        <Disclosure title="설명">
          <dl className="space-y-3 text-table">
            <div>
              <dt className="font-medium">앱 표</dt>
              <dd className="mt-0.5 text-muted-foreground">
                추이는 기간 안 UTC 일별 세션이고 가장 새 막대가 오른쪽이다. &ldquo;짝 없음&rdquo;은 짝이 되는 screen_exit이 없는 screen_enter의 비율이다. 이 값이 오르면 그
                앱의 체류 시간 수치는 더 이상 의미가 없다.
              </dd>
            </div>
            <div>
              <dt className="font-medium">체류 시간</dt>
              <dd className="mt-0.5 text-muted-foreground">
                짝이 맞는 screen_enter / screen_exit 이벤트로 잰다. 다음 이벤트까지의 간격으로 짐작하지 않는다.
              </dd>
            </div>
            <div>
              <dt className="font-medium">다시 계산</dt>
              <dd className="mt-0.5 text-muted-foreground">
                집계는 하루에 한 번 계산해 두고 쓴다. 체류 상한이나 너무 짧은 방문의 기준을 바꾸면 지난 날은 옛 값 그대로여서, 저장된 원본 이벤트로 다시 만든다. 바깥으로
                나가는 호출은 없다. 번들 ID가 다른 앱은 표의 &ldquo;연결&rdquo;에서 직접 이어 준다.
              </dd>
            </div>
          </dl>
        </Disclosure>
      </div>

      <ConfirmDialog
        open={confirm === 'pull'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="지금 수집할까"
        description="수집기 워커에서 새 이벤트를 가져오고 집계를 갱신한다. 5분마다 도는 작업을 한 번 앞당기는 것이다."
        confirmLabel="수집"
        onConfirm={async () => {
          setConfirm(null)
          try {
            await pull.mutateAsync()
          } catch {
            /* the toast and the page already say why */
          }
        }}
      />
      <ConfirmDialog
        open={confirm === 'backfill'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="최근 30일을 다시 계산할까"
        description="저장된 원본 이벤트로 일별 집계를 다시 만든다. 바깥으로 나가는 호출은 없고, 이벤트가 많으면 몇 분 걸린다."
        confirmLabel="다시 계산"
        onConfirm={async () => {
          setConfirm(null)
          try {
            await backfill.mutateAsync()
          } catch {
            /* the toast and the page already say why */
          }
        }}
      />
    </>
  )
}
